<template lang="pug">
#send-document-detail(v-cloak)
  .box
    .box-header.register-header
      .title
        span 文書送付の確認
      .register-row(v-show="errorMessage")
        .register-group
          .register-value
            .error-message {{ errorMessage }}
    .box-body
      b-tabs
        b-tab.tab(title="送付内容")
          SendDocumentDetailTab(ref="sendDocumentDetailTab" @onRemind="showRemind")
        b-tab.tab(title="再送履歴")
          SendDocumentRemindHistoryTab(ref="sendDocumentRemindHistoryTab")

    .box-footer.with-border.register-footer
      .buttons
        button.btn.cancel-btn(@click="gotoSendDocumentList" type="button") 戻る

  PageLoading(:isBusy="isBusy" :message="loadingMessage")

  RemindModal(v-model="showRemindModal" :modalTitle.sync="remindModalTitle" :targets="remindTargets" :remindTitle.sync="remindTitle"
    :remindText.sync="remindText" :showValidationAlert.sync="showValidationAlert" 
    @ok="confirmSendRemind(sendDocumentRemindModel)")
  OkCancelModal(v-model="showSendRemindModal" :title="sendRemindModalTitle" @ok="sendRemind(sendDocumentRemindModel)")

  OkModal(v-model="showSendRemindSuccessModal" title="送信を受け付けました" @ok="gotoSendDocumentList")
</template>

<script lang="ts">
import { Component, Inject, Vue } from "vue-property-decorator";
import { userStore } from "@/store/store";

import { PcUrl } from "@/commons/constants/PcUrl";
import SystemError from "@/commons/models/SystemError";

import SendDocumentsService, { Type, SendDocumentGetModel, SendDocumentRemindPostModel } from "@/commons/services/SendDocuments";
import { GroupType } from "@/commons/services/SendMessageGroups";

import PageLoading from "@/pc/basics/PageLoading.vue";
import OkModal from "@/pc/basics/OkModal.vue";
import RemindModal from "@/pc/containers/sendDocuments/RemindModal.vue";
import OkCancelModal from "@/pc/basics/OkCancelModal.vue";

import SendDocumentDetailTab from "@/pc/containers/sendDocuments/SendDocumentDetailTab.vue";
import SendDocumentRemindHistoryTab from "@/pc/containers/sendDocuments/SendDocumentRemindHistoryTab.vue";
import { OptionTargetFamilyTableItem } from "@/pc/containers/sendDocuments/OptionTargetFamilyTable.vue";
import { OptionTargetOtherUserTableItem } from "@/pc/containers/sendDocuments/OptionTargetOtherUserTable.vue";
import { SenderType } from "@/commons/services/Families";

/**
 * ### 文書送付確認画面コンポーネント
 */
@Component({
  components: {
    PageLoading,
    OkModal,
    RemindModal,
    OkCancelModal,
    SendDocumentDetailTab,
    SendDocumentRemindHistoryTab
  },
  $_veeValidate: { validator: "new" }
})
export default class SendDocumentDetail extends Vue {
  /** 文書送付サービス */
  @Inject("SendDocumentsService") sendDocumentsService!: SendDocumentsService;

  /** ページ全体ローディング表示可否 */
  isBusy: boolean = false;
  /** ローディングメッセージ */
  loadingMessage: string = "";

  /** 文書送付再送用Model */
  sendDocumentRemindModel: SendDocumentRemindModel | null = null;

  /** バリデーションアラート表示可否 */
  showValidationAlert: boolean = false;

  /** 再送内容入力モーダル表示可否 */
  showRemindModal: boolean = false;
  /** 再送内容入力モーダルタイトル */
  remindModalTitle: string = "";
  /** 再送宛先 */
  remindTargets: Array<OptionTargetFamilyTableItem | OptionTargetOtherUserTableItem> = [];
  /** 再送タイトル */
  remindTitle: string = "";
  /** 再送本文 */
  remindText: string = "";
  /** 再送確認モーダル表示可否 */
  showSendRemindModal: boolean = false;
  /** 再送確認モーダルタイトル */
  sendRemindModalTitle: string = "";
  /** 再送完了モーダル表示可否 */
  showSendRemindSuccessModal: boolean = false;

  /** エラーメッセージ */
  errorMessage: string = "";

  /**
   * ### vue lifecycle created
   */
  public async mounted(): Promise<void> {
    return;
  }

  /**
   * ### リマインド入力モーダルを表示する
   */
  public showRemind(sendDocumentRemindModel: SendDocumentRemindModel): void {
    if (sendDocumentRemindModel.sendDocument.needAgreement == true) {
      this.remindModalTitle = "未同意者に再送";
    } else if (sendDocumentRemindModel.sendDocument.needConfirm == true) {
      this.remindModalTitle = "未確認者に再送";
    }

    this.sendDocumentRemindModel = sendDocumentRemindModel;
    this.remindTargets = this.remindSendTargetLabel(sendDocumentRemindModel);
    this.remindTitle = sendDocumentRemindModel.sendDocument.title.slice();
    this.remindText = sendDocumentRemindModel.sendDocument.text.slice();

    this.showRemindModal = true;

    return;
  }

  /**
   * ### リマインド宛先文字列を返す
   */
  public remindSendTargetLabel(sendDocumentRemindModel: SendDocumentRemindModel): Array<OptionTargetFamilyTableItem | OptionTargetOtherUserTableItem> {
    if (sendDocumentRemindModel.sendDocument == null) {
      return [];
    }

    let sendTargets: Array<OptionTargetFamilyTableItem | OptionTargetOtherUserTableItem> = [];
    switch (sendDocumentRemindModel.sendDocument.type) {
      case Type.ALL_FAMILY:
      case Type.SELECTED_FAMILY:
      case Type.RESIDENT_GROUP: {
        sendTargets = sendDocumentRemindModel.familyNonReplyItems;
        break;
      }
      case Type.SELECTED_OTHER_USER:
      case Type.OTHER_USER_GROUP: {
        sendTargets = sendDocumentRemindModel.otherUserNonReplyItems;
        break;
      }
      case Type.SEND_MESSAGE_GROUP: {
        if (sendDocumentRemindModel.sendDocument.sendMessageGroupInfos == null) {
          let systemError: SystemError = new SystemError(`sendMessageGroupInfos is null. type:${sendDocumentRemindModel.sendDocument.type}`);
          systemError.statusCode = 404;
          throw systemError;
        }

        if (sendDocumentRemindModel.sendDocument.sendMessageGroupInfos[0].type == GroupType.FAMILY) {
          sendTargets = sendDocumentRemindModel.familyNonReplyItems;
          break;
        } else if (sendDocumentRemindModel.sendDocument.sendMessageGroupInfos[0].type == GroupType.OTHER_USER) {
          sendTargets = sendDocumentRemindModel.otherUserNonReplyItems;
          break;
        }
      }
    }

    return sendTargets;
  }

  /**
   * ### 再送確認モーダルを表示する
   */
  public async confirmSendRemind(sendDocumentRemindModel: SendDocumentRemindModel): Promise<void> {
    const validationResult: boolean = await this.$validator.validateAll();
    if (validationResult) {
      this.showValidationAlert = false;
      if (sendDocumentRemindModel.sendDocument.needAgreement == true) {
        this.sendRemindModalTitle = "未同意者に再送します。よろしいですか？";
      } else if (sendDocumentRemindModel.sendDocument.needConfirm == true) {
        this.sendRemindModalTitle = "未確認者に再送します。よろしいですか？";
      }
      this.showSendRemindModal = true;
    } else {
      this.showValidationAlert = true;
    }

    return;
  }

  /**
   * ### 再送する
   *
   * - 再送内容入力モーダルを非表示にする
   * - 再送内容入力モーダルで入力された内容で再送する
   * - 再送完了モーダルを表示する
   */
  public async sendRemind(sendDocumentRemindModel: SendDocumentRemindModel): Promise<void> {
    this.showRemindModal = false;
    this.loadingMessage = "送信中...";
    this.isBusy = true;

    const sendDocument: SendDocumentGetModel = sendDocumentRemindModel.sendDocument;
    const requestBody: SendDocumentRemindPostModel = this.createRequestBody(sendDocumentRemindModel);

    switch (sendDocumentRemindModel.sendDocument.type) {
      case Type.ALL_FAMILY:
      case Type.SELECTED_FAMILY:
      case Type.RESIDENT_GROUP: {
        await this.sendDocumentsService.sendRemindToFamilies(userStore, sendDocument.mainId as number, requestBody);
        break;
      }
      case Type.SELECTED_OTHER_USER:
      case Type.OTHER_USER_GROUP: {
        await this.sendDocumentsService.sendRemindToOtherUserGroups(userStore, sendDocument.mainId as number, requestBody);
        break;
      }
      case Type.SEND_MESSAGE_GROUP: {
        if (!sendDocument.sendMessageGroupInfos || sendDocument.sendMessageGroupInfos.length < 1) {
          throw new SystemError(`error sendMessageGroupInfos undefined or length < 1. id: ${sendDocument.id}`);
        }

        if (sendDocument.sendMessageGroupInfos[0].type == GroupType.FAMILY) {
          await this.sendDocumentsService.sendRemindToFamilies(userStore, sendDocument.mainId as number, requestBody);
        } else if (sendDocument.sendMessageGroupInfos[0].type == GroupType.OTHER_USER) {
          await this.sendDocumentsService.sendRemindToOtherUserGroups(userStore, sendDocument.mainId as number, requestBody);
        }
        break;
      }
    }

    this.isBusy = false;
    this.showSendRemindSuccessModal = true;

    return;
  }

  /**
   * ### 再送リクエストボディを作成する
   */
  public createRequestBody(sendDocumentRemindModel: SendDocumentRemindModel): SendDocumentRemindPostModel {
    const receivers: SendDocumentFamilyReceivers | SendDocumentOtherUserReceivers = this.getRemindReceivers(sendDocumentRemindModel);
    const requestBody: SendDocumentRemindPostModel = {
      receivers: receivers.receivers,
      receiversByLine: receivers.receiversByLine,
      receiversByMail: receivers.receiversByMail,
      title: this.remindTitle,
      text: this.remindText
    };

    return requestBody;
  }

  /**
   * ### リマインド宛先を返す
   */
  public getRemindReceivers(sendDocumentRemindModel: SendDocumentRemindModel): SendDocumentFamilyReceivers | SendDocumentOtherUserReceivers {
    if (sendDocumentRemindModel.sendDocument == null) {
      return {
        receivers: [],
        receiversByLine: [],
        receiversByMail: []
      };
    }

    const sendDocument: SendDocumentGetModel = sendDocumentRemindModel.sendDocument;

    const sendDocumentFamilyReceivers: Array<string> = [];
    const sendDocumentFamilyReceiversByLine: Array<string> = [];
    const sendDocumentFamilyReceiversByMail: Array<string> = [];

    const sendDocumentOtherUserReceivers: Array<string> = [];
    const sendDocumentOtherUserReceiversByLine: Array<string> = [];
    const sendDocumentOtherUserReceiversByMail: Array<string> = [];

    switch (sendDocument.type) {
      case Type.ALL_FAMILY:
      case Type.SELECTED_FAMILY:
      case Type.RESIDENT_GROUP: {
        for (const nonReplyFamily of sendDocumentRemindModel.familyNonReplyItems) {
          if (nonReplyFamily.isTarget == true) {
            sendDocumentFamilyReceivers.push(nonReplyFamily.residentsSortKey);

            if (nonReplyFamily.senderType == SenderType.LINE) {
              sendDocumentFamilyReceiversByLine.push(nonReplyFamily.residentsSortKey);
            } else if (nonReplyFamily.senderType == SenderType.MAIL) {
              sendDocumentFamilyReceiversByMail.push(nonReplyFamily.residentsSortKey);
            }
          }
        }
        return {
          receivers: sendDocumentFamilyReceivers,
          receiversByLine: sendDocumentFamilyReceiversByLine,
          receiversByMail: sendDocumentFamilyReceiversByMail
        } as SendDocumentFamilyReceivers;
      }
      case Type.SELECTED_OTHER_USER:
      case Type.OTHER_USER_GROUP: {
        for (const nonReplyOtherUser of sendDocumentRemindModel.otherUserNonReplyItems) {
          if (nonReplyOtherUser.isTarget == true) {
            sendDocumentOtherUserReceivers.push(nonReplyOtherUser.otherUserGroupsSortKey);

            if (nonReplyOtherUser.senderType == SenderType.LINE) {
              sendDocumentOtherUserReceiversByLine.push(nonReplyOtherUser.otherUserGroupsSortKey);
            } else if (nonReplyOtherUser.senderType == SenderType.MAIL) {
              sendDocumentOtherUserReceiversByMail.push(nonReplyOtherUser.otherUserGroupsSortKey);
            }
          }
        }
        return {
          receivers: sendDocumentOtherUserReceivers,
          receiversByLine: sendDocumentOtherUserReceiversByLine,
          receiversByMail: sendDocumentOtherUserReceiversByMail
        } as SendDocumentOtherUserReceivers;
      }
      case Type.SEND_MESSAGE_GROUP: {
        if (sendDocument.sendMessageGroupInfos == null) {
          let systemError: SystemError = new SystemError(`sendMessageGroupInfos is null. type:${sendDocument.type}`);
          systemError.statusCode = 404;
          throw systemError;
        }

        if (sendDocument.sendMessageGroupInfos[0].type == GroupType.FAMILY) {
          for (const nonReplyFamily of sendDocumentRemindModel.familyNonReplyItems) {
            if (nonReplyFamily.isTarget == true) {
              sendDocumentFamilyReceivers.push(nonReplyFamily.residentsSortKey);

              if (nonReplyFamily.senderType == SenderType.LINE) {
                sendDocumentFamilyReceiversByLine.push(nonReplyFamily.residentsSortKey);
              } else if (nonReplyFamily.senderType == SenderType.MAIL) {
                sendDocumentFamilyReceiversByMail.push(nonReplyFamily.residentsSortKey);
              }
            }
          }
          return {
            receivers: sendDocumentFamilyReceivers,
            receiversByLine: sendDocumentFamilyReceiversByLine,
            receiversByMail: sendDocumentFamilyReceiversByMail
          } as SendDocumentFamilyReceivers;
        } else if (sendDocument.sendMessageGroupInfos[0].type == GroupType.OTHER_USER) {
          for (const nonReplyOtherUser of sendDocumentRemindModel.otherUserNonReplyItems) {
            if (nonReplyOtherUser.isTarget == true) {
              sendDocumentOtherUserReceivers.push(nonReplyOtherUser.otherUserGroupsSortKey);

              if (nonReplyOtherUser.senderType == SenderType.LINE) {
                sendDocumentOtherUserReceiversByLine.push(nonReplyOtherUser.otherUserGroupsSortKey);
              } else if (nonReplyOtherUser.senderType == SenderType.MAIL) {
                sendDocumentOtherUserReceiversByMail.push(nonReplyOtherUser.otherUserGroupsSortKey);
              }
            }
          }
          return {
            receivers: sendDocumentOtherUserReceivers,
            receiversByLine: sendDocumentOtherUserReceiversByLine,
            receiversByMail: sendDocumentOtherUserReceiversByMail
          } as SendDocumentOtherUserReceivers;
        }
      }
    }

    return {
      receivers: [],
      receiversByLine: [],
      receiversByMail: []
    };
  }

  /**
   * ### 文書送付一覧に遷移する
   */
  public gotoSendDocumentList(): void {
    this.$router.push(PcUrl.SendDocumentList);
    return;
  }

  /**
   * ### 各タブの画面情報再作成
   *
   * - 再作成対象
   *   - 送付内容タブ
   *   - 再送付履歴タブ
   */
  public async refreshTabs(): Promise<void> {
    const promises: Array<Promise<void>> = [];

    const sendDocumentDetailTab: SendDocumentDetailTab | Vue | Element | Vue[] | Element[] = this.$refs.sendDocumentDetailTab;
    if (sendDocumentDetailTab instanceof SendDocumentDetailTab) {
      promises.push(sendDocumentDetailTab.refresh());
    }

    const sendDocumentRemindHistoryTab: SendDocumentRemindHistoryTab | Vue | Element | Vue[] | Element[] = this.$refs.sendDocumentRemindHistoryTab;
    if (sendDocumentRemindHistoryTab instanceof SendDocumentRemindHistoryTab) {
      promises.push(sendDocumentRemindHistoryTab.refresh());
    }

    await Promise.all(promises);

    return;
  }
}

export interface SendDocumentRemindModel {
  sendDocument: SendDocumentGetModel;
  familyNonReplyItems: Array<OptionTargetFamilyTableItem>;
  otherUserNonReplyItems: Array<OptionTargetOtherUserTableItem>;
}

interface SendDocumentFamilyReceivers {
  receivers: Array<string>;
  receiversByLine: Array<string>;
  receiversByMail: Array<string>;
}

interface SendDocumentOtherUserReceivers {
  receivers: Array<string>;
  receiversByLine: Array<string>;
  receiversByMail: Array<string>;
}
</script>

<style lang="scss" scoped>
#send-document-detail {
  max-width: 1280px;
  margin-left: auto;
  margin-right: auto;

  .cancel-btn {
    width: 120px;
  }
}
</style>
